import { useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format, startOfDay, endOfDay, isToday } from "date-fns";
import {
  Search,
  Loader2,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Calendar,
  Database,
  Globe,
  RefreshCw,
  User,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { API, formatNumber, getPlatformBadgeClass, getPlatformName } from "./constants";

// Translations
const translations = {
  en: {
    title: "Keyword Search",
    subtitle: "Search and analyze content across all platforms",
    keywordPlaceholder: "Enter keyword...",
    searchBtn: "Search",
    searching: "Searching...",
    platforms: "Platforms",
    allPlatforms: "All Platforms",
    dateRange: "Date Range",
    startDate: "Start",
    endDate: "End",
    today: "Today",
    clearDates: "Clear dates",
    searchMode: "Search Mode",
    modeApi: "Real-time API",
    modeDb: "Database",
    modeBoth: "Both",
    results: "Results",
    totalResults: "Total Results",
    noResults: "No results found",
    posts: "posts",
    views: "Views",
    likes: "Likes",
    comments: "Comments",
    shares: "Shares",
    engagement: "Engagement",
    viewPost: "View",
    expandAll: "Expand All",
    collapseAll: "Collapse All",
    loadMore: "Load More",
    apiNote: "Real-time search via platform APIs",
    dbNote: "Search from synced posts in database",
  },
  ko: {
    title: "키워드 검색",
    subtitle: "모든 플랫폼에서 콘텐츠 검색 및 분석",
    keywordPlaceholder: "키워드 입력...",
    searchBtn: "검색",
    searching: "검색 중...",
    platforms: "플랫폼",
    allPlatforms: "전체 플랫폼",
    dateRange: "기간",
    startDate: "시작일",
    endDate: "종료일",
    today: "오늘",
    clearDates: "날짜 초기화",
    searchMode: "검색 모드",
    modeApi: "실시간 API",
    modeDb: "데이터베이스",
    modeBoth: "통합 검색",
    results: "검색 결과",
    totalResults: "총 결과",
    noResults: "검색 결과가 없습니다",
    posts: "개 게시물",
    views: "조회수",
    likes: "좋아요",
    comments: "댓글",
    shares: "공유",
    engagement: "참여도",
    viewPost: "보기",
    expandAll: "모두 펼치기",
    collapseAll: "모두 접기",
    loadMore: "더 보기",
    apiNote: "플랫폼 API를 통한 실시간 검색",
    dbNote: "동기화된 게시물에서 검색",
  },
  zh: {
    title: "关键词搜索",
    subtitle: "跨平台搜索和分析内容",
    keywordPlaceholder: "输入关键词...",
    searchBtn: "搜索",
    searching: "搜索中...",
    platforms: "平台",
    allPlatforms: "全部平台",
    dateRange: "日期范围",
    startDate: "开始",
    endDate: "结束",
    today: "今天",
    clearDates: "清除日期",
    searchMode: "搜索模式",
    modeApi: "实时API",
    modeDb: "数据库",
    modeBoth: "综合搜索",
    results: "搜索结果",
    totalResults: "总结果",
    noResults: "未找到结果",
    posts: "个帖子",
    views: "浏览量",
    likes: "点赞",
    comments: "评论",
    shares: "分享",
    engagement: "互动率",
    viewPost: "查看",
    expandAll: "全部展开",
    collapseAll: "全部折叠",
    loadMore: "加载更多",
    apiNote: "通过平台API实时搜索",
    dbNote: "从已同步的帖子中搜索",
  },
};

// Platform configurations - All platforms support API search
const ALL_PLATFORMS = [
  { id: "youtube", name: "YouTube", color: "#FF0000", searchSupported: true },
  { id: "tiktok", name: "TikTok", color: "#69C9D0", searchSupported: true },
  { id: "instagram", name: "Instagram", color: "#E1306C", searchSupported: true },
  { id: "douyin", name: "Douyin", color: "#25F4EE", searchSupported: true },
  { id: "xiaohongshu", name: "Xiaohongshu", color: "#FF2442", searchSupported: true },
  { id: "bilibili", name: "Bilibili", color: "#00A1D6", searchSupported: true },
  { id: "weibo", name: "Weibo", color: "#E6162D", searchSupported: true },
];

// Platform Result Card Component
const PlatformResultCard = ({ platform, results, lang, isOpen, onToggle }) => {
  const t = translations[lang] || translations.en;
  const platformInfo = ALL_PLATFORMS.find(p => p.id === platform) || {};
  
  const totalViews = results.reduce((sum, r) => sum + (r.views || 0), 0);
  const totalLikes = results.reduce((sum, r) => sum + (r.likes || 0), 0);
  const avgEngagement = results.length > 0 
    ? results.reduce((sum, r) => {
        if (r.views > 0) return sum + ((r.likes + r.comments) / r.views * 100);
        return sum;
      }, 0) / results.length
    : 0;

  const getPostUrl = (post) => {
    // Use post_url if available (from crawler/API)
    if (post.post_url) {
      return post.post_url;
    }
    
    const platform = post.platform;
    const id = post.platform_id || post.id || post.post_id;
    const urls = {
      youtube: `https://www.youtube.com/watch?v=${id}`,
      tiktok: `https://www.tiktok.com/@${post.author_handle || 'user'}/video/${id}`,
      instagram: `https://www.instagram.com/p/${id}`,
      douyin: `https://www.douyin.com/video/${id}`,
      xiaohongshu: `https://www.xiaohongshu.com/explore/${id}`,
      bilibili: `https://www.bilibili.com/video/${id}`,
      weibo: `https://weibo.com/${post.author_id}/${id}`,
    };
    return urls[platform] || '#';
  };

  // Get user profile URL for xiaohongshu
  const getUserProfileUrl = (post) => {
    // Use user_profile_url if provided by crawler
    if (post.user_profile_url) {
      return post.user_profile_url;
    }
    
    // Construct from channel_id
    if (post.channel_id && post.platform === 'xiaohongshu') {
      return `https://www.xiaohongshu.com/user/profile/${post.channel_id}`;
    }
    
    return null;
  };

  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <Card className="bg-card border-border/50">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-accent/5 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge 
                  className={getPlatformBadgeClass(platform)}
                  style={{ borderColor: platformInfo.color }}
                >
                  {getPlatformName(platform, lang)}
                </Badge>
                <span className="text-lg font-semibold">
                  {results.length} {t.posts}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden sm:flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {formatNumber(totalViews)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="h-4 w-4" />
                    {formatNumber(totalLikes)}
                  </span>
                  {avgEngagement > 0 && (
                    <span className="text-primary font-medium">
                      ER: {avgEngagement.toFixed(2)}%
                    </span>
                  )}
                </div>
                {isOpen ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {results.map((post, idx) => (
                  <div 
                    key={post.id || post.post_id || idx}
                    className="p-3 rounded-lg bg-accent/5 hover:bg-accent/10 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm line-clamp-2">
                          {post.title || post.description?.slice(0, 100) || `Post ${(post.post_id || post.id || '').slice(0, 8)}...`}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          {post.author_name || post.channel_name || 'Unknown'}
                          {post.published_at && (
                            <span className="ml-2">
                              • {post.published_at.slice(0, 10)}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {/* For xiaohongshu: Show user profile button as primary */}
                        {platform === 'xiaohongshu' && getUserProfileUrl(post) ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => window.open(getUserProfileUrl(post), '_blank')}
                              title={lang === 'ko' ? '작성자 프로필' : lang === 'zh' ? '用户资料' : 'User Profile'}
                            >
                              <User className="h-3 w-3 mr-1" />
                              {lang === 'ko' ? '프로필' : lang === 'zh' ? '资料' : 'Profile'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(getPostUrl(post), '_blank')}
                              title={lang === 'ko' ? '게시물 보기' : lang === 'zh' ? '查看帖子' : 'View Post'}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(getPostUrl(post), '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      {post.views > 0 && (
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {formatNumber(post.views)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Heart className="h-3 w-3" />
                        {formatNumber(post.likes || 0)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" />
                        {formatNumber(post.comments || 0)}
                      </span>
                      {post.shares > 0 && (
                        <span className="flex items-center gap-1">
                          <Share2 className="h-3 w-3" />
                          {formatNumber(post.shares)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

// Main Component
const KeywordSearchPageV2 = ({ lang = "en" }) => {
  const t = translations[lang] || translations.en;
  
  // Get today's date for default
  const today = useMemo(() => startOfDay(new Date()), []);
  
  // State - default to today for both start and end date
  const [keyword, setKeyword] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [searchMode, setSearchMode] = useState("api"); // Default to API search for external content
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedPlatforms, setExpandedPlatforms] = useState(new Set());
  
  // Helper to check if using default "today" filter
  const isDefaultDateRange = useMemo(() => {
    if (!startDate || !endDate) return false;
    return isToday(startDate) && isToday(endDate);
  }, [startDate, endDate]);

  // Reset dates to today
  const resetDatesToToday = () => {
    setStartDate(today);
    setEndDate(today);
  };

  // Clear all date filters
  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const togglePlatform = (platformId) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handleSearch = useCallback(async () => {
    if (!keyword.trim()) {
      toast.error(lang === "ko" ? "키워드를 입력해주세요" : lang === "zh" ? "请输入关键词" : "Please enter a keyword");
      return;
    }

    setLoading(true);
    setResults({ results: {}, total: 0, keyword: keyword.trim() });

    try {
      // Filter to only search supported platforms
      const supportedPlatforms = ALL_PLATFORMS.filter(p => p.searchSupported).map(p => p.id);
      
      const platformsToSearch = selectedPlatforms.length > 0 
        ? selectedPlatforms.filter(p => supportedPlatforms.includes(p))
        : supportedPlatforms;

      const allResults = {};
      let totalCount = 0;

      // API Search with SSE streaming - results arrive platform by platform
      if (searchMode === "api" || searchMode === "both") {
        try {
          const params = new URLSearchParams({
            keyword: keyword.trim(),
            platforms: platformsToSearch.join(","),
            max_results_per_platform: "30",
          });
          if (startDate) params.append("published_after", new Date(startDate).toISOString());
          if (endDate) params.append("published_before", new Date(endDate).toISOString());

          await new Promise((resolve, reject) => {
            const eventSource = new EventSource(`${API}/search/keyword/stream?${params.toString()}`);
            
            eventSource.onmessage = (event) => {
              try {
                const msg = JSON.parse(event.data);
                
                if (msg.type === "result") {
                  const platform = msg.platform;
                  const posts = msg.data?.results || [];
                  if (posts.length > 0) {
                    posts.sort((a, b) => (b.likes || 0) - (a.likes || 0));
                    allResults[platform] = posts;
                    totalCount += posts.length;
                    // Progressive update - show results immediately
                    setResults(prev => ({
                      results: { ...(prev?.results || {}), [platform]: posts },
                      total: (prev?.total || 0) + posts.length,
                      keyword: keyword.trim(),
                    }));
                    setExpandedPlatforms(prev => new Set([...prev, platform]));
                  }
                } else if (msg.type === "error") {
                  console.warn(`Search error for ${msg.platform}:`, msg.error);
                } else if (msg.type === "done") {
                  eventSource.close();
                  resolve();
                }
              } catch (parseErr) {
                console.error("SSE parse error:", parseErr);
              }
            };

            eventSource.onerror = () => {
              eventSource.close();
              // If we already have some results, resolve successfully
              if (totalCount > 0) {
                resolve();
              } else {
                reject(new Error("Stream connection failed"));
              }
            };

            // Safety timeout - close after 90s
            setTimeout(() => {
              eventSource.close();
              resolve();
            }, 90000);
          });
        } catch (apiError) {
          console.error("API search error:", apiError);
          toast.error(lang === "ko" ? "외부 검색 중 오류 발생" : lang === "zh" ? "外部搜索出错" : "External search error");
        }
      }

      // Database Search
      if (searchMode === "db" || searchMode === "both") {
        try {
          let dbUrl = `${API}/posts?limit=200&search=${encodeURIComponent(keyword.trim())}`;
          
          if (selectedPlatforms.length > 0) {
            dbUrl += `&platform=${selectedPlatforms.join(",")}`;
          }
          if (startDate) {
            dbUrl += `&start_date=${format(startDate, "yyyy-MM-dd")}`;
          }
          if (endDate) {
            dbUrl += `&end_date=${format(endDate, "yyyy-MM-dd")}`;
          }

          const dbResponse = await axios.get(dbUrl);
          
          if (dbResponse.data && Array.isArray(dbResponse.data)) {
            dbResponse.data.forEach(post => {
              const platform = post.platform;
              if (!allResults[platform]) allResults[platform] = [];
              
              // Avoid duplicates
              const exists = allResults[platform].some(p => 
                p.platform_id === post.platform_id || p.id === post.id
              );
              if (!exists) {
                allResults[platform].push(post);
                totalCount++;
              }
            });
          }

          // Update results with DB data merged
          setResults({
            results: { ...allResults },
            total: totalCount,
            keyword: keyword.trim(),
          });
          setExpandedPlatforms(new Set(Object.keys(allResults)));
        } catch (dbError) {
          console.error("DB search error:", dbError);
        }
      }

      if (totalCount > 0) {
        toast.success(
          lang === "ko" 
            ? `${totalCount}개의 결과를 찾았습니다` 
            : lang === "zh"
            ? `找到 ${totalCount} 条结果`
            : `Found ${totalCount} results`
        );
      } else {
        toast.info(t.noResults);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error(lang === "ko" ? "검색 중 오류가 발생했습니다" : lang === "zh" ? "搜索失败" : "Search failed");
    } finally {
      setLoading(false);
    }
  }, [keyword, selectedPlatforms, startDate, endDate, searchMode, lang, t.noResults]);

  const expandAll = () => {
    if (results?.results) {
      setExpandedPlatforms(new Set(Object.keys(results.results)));
    }
  };

  const collapseAll = () => {
    setExpandedPlatforms(new Set());
  };

  return (
    <div className="space-y-6" data-testid="keyword-search-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
          <Search className="h-6 w-6 text-primary" />
          {t.title}
        </h1>
        <p className="text-muted-foreground mt-1">{t.subtitle}</p>
      </div>

      {/* Search Controls */}
      <Card className="bg-card border-border/50">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Keyword Input + Search Button */}
            <div className="flex gap-2">
              <Input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder={t.keywordPlaceholder}
                className="flex-1"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                data-testid="keyword-input"
              />
              <Button 
                onClick={handleSearch} 
                disabled={loading}
                className="min-w-[100px]"
                data-testid="search-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t.searching}
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    {t.searchBtn}
                  </>
                )}
              </Button>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Platform Selection */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    {t.platforms}: {selectedPlatforms.length > 0 ? selectedPlatforms.length : t.allPlatforms}
                    <ChevronDown className="h-4 w-4 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64" align="start">
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground mb-2 pb-2 border-b border-border">
                      {lang === "ko" ? "검색 가능한 플랫폼" : lang === "zh" ? "可搜索平台" : "Searchable Platforms"}
                    </div>
                    {ALL_PLATFORMS.map((platform) => (
                      <div key={platform.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={platform.id}
                          checked={selectedPlatforms.includes(platform.id)}
                          onCheckedChange={() => togglePlatform(platform.id)}
                          disabled={!platform.searchSupported}
                        />
                        <label
                          htmlFor={platform.id}
                          className={`text-sm font-medium leading-none flex items-center gap-2 ${
                            !platform.searchSupported ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          }`}
                        >
                          <span 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: platform.color }}
                          />
                          {platform.name}
                          {!platform.searchSupported && (
                            <Badge variant="outline" className="text-[10px] py-0 px-1 text-muted-foreground">
                              {platform.unavailableReason || "N/A"}
                            </Badge>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Date Range - Start */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={`h-9 ${isDefaultDateRange ? "border-blue-500/50 bg-blue-500/5" : ""}`}>
                    <Calendar className="h-4 w-4 mr-2" />
                    {startDate ? (
                      isToday(startDate) ? (
                        <span className="text-blue-400">{t.today}</span>
                      ) : (
                        format(startDate, "yyyy-MM-dd")
                      )
                    ) : t.startDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Date Range - End */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={`h-9 ${isDefaultDateRange ? "border-blue-500/50 bg-blue-500/5" : ""}`}>
                    <Calendar className="h-4 w-4 mr-2" />
                    {endDate ? (
                      isToday(endDate) ? (
                        <span className="text-blue-400">{t.today}</span>
                      ) : (
                        format(endDate, "yyyy-MM-dd")
                      )
                    ) : t.endDate}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              
              {/* Today indicator */}
              {isDefaultDateRange && (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-400 border-blue-500/30 text-xs">
                  {t.today}
                </Badge>
              )}

              {/* Search Mode */}
              <div className="flex items-center gap-1 border rounded-md">
                <Button
                  variant={searchMode === "both" ? "default" : "ghost"}
                  size="sm"
                  className="h-9 rounded-r-none"
                  onClick={() => setSearchMode("both")}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  {t.modeBoth}
                </Button>
                <Button
                  variant={searchMode === "api" ? "default" : "ghost"}
                  size="sm"
                  className="h-9 rounded-none border-x"
                  onClick={() => setSearchMode("api")}
                >
                  <Globe className="h-4 w-4 mr-1" />
                  API
                </Button>
                <Button
                  variant={searchMode === "db" ? "default" : "ghost"}
                  size="sm"
                  className="h-9 rounded-l-none"
                  onClick={() => setSearchMode("db")}
                >
                  <Database className="h-4 w-4 mr-1" />
                  DB
                </Button>
              </div>

              {/* Clear Dates / Reset to Today */}
              {(startDate || endDate) && !isDefaultDateRange && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetDatesToToday}
                  className="text-blue-400 hover:text-blue-300"
                >
                  {t.today}
                </Button>
              )}
              {(startDate || endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearDates}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {t.clearDates}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      {(results || loading) && (
        <div className="space-y-4">
          {/* Summary Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold">
                {t.results}: &quot;{results?.keyword || keyword.trim()}&quot;
              </h2>
              <Badge variant="secondary" className="text-base">
                {results?.total || 0} {t.totalResults}
              </Badge>
              {loading && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {lang === "ko" ? "검색 중..." : lang === "zh" ? "搜索中..." : "Searching..."}
                </div>
              )}
            </div>
            {results?.results && Object.keys(results.results).length > 0 && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={expandAll}>
                {t.expandAll}
              </Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>
                {t.collapseAll}
              </Button>
            </div>
            )}
          </div>

          {/* Platform Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {ALL_PLATFORMS.filter(p => p.searchSupported).map((platform) => {
              const platformResults = results?.results?.[platform.id] || [];
              const count = platformResults.length;
              const isSearching = loading && !results?.results?.[platform.id];
              
              return (
                <Card 
                  key={platform.id}
                  className={`bg-card border-border/50 transition-all ${
                    count > 0 ? 'cursor-pointer hover:border-primary/50' : isSearching ? 'opacity-70' : 'opacity-50'
                  }`}
                  onClick={() => {
                    if (count > 0) {
                      setExpandedPlatforms(prev => {
                        const next = new Set(prev);
                        if (next.has(platform.id)) {
                          next.delete(platform.id);
                        } else {
                          next.add(platform.id);
                        }
                        return next;
                      });
                    }
                  }}
                >
                  <CardContent className="p-3 text-center">
                    {isSearching ? (
                      <Loader2 
                        className="h-5 w-5 mx-auto animate-spin" 
                        style={{ color: platform.color }}
                      />
                    ) : (
                    <div 
                      className="text-2xl font-bold"
                      style={{ color: count > 0 ? platform.color : undefined }}
                    >
                      {count}
                    </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {platform.name}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Platform Result Lists */}
          <div className="space-y-3">
            {results?.results && Object.entries(results.results)
              .filter(([_, posts]) => posts.length > 0)
              .sort((a, b) => b[1].length - a[1].length)
              .map(([platform, posts]) => (
                <PlatformResultCard
                  key={platform}
                  platform={platform}
                  results={posts}
                  lang={lang}
                  isOpen={expandedPlatforms.has(platform)}
                  onToggle={() => {
                    setExpandedPlatforms(prev => {
                      const next = new Set(prev);
                      if (next.has(platform)) {
                        next.delete(platform);
                      } else {
                        next.add(platform);
                      }
                      return next;
                    });
                  }}
                />
              ))}
          </div>

          {/* No Results Message */}
          {!loading && results?.total === 0 && (
            <Card className="bg-card border-border/50">
              <CardContent className="py-12 text-center">
                <Search className="h-12 w-12 mx-auto text-muted-foreground/50" />
                <p className="mt-4 text-muted-foreground">{t.noResults}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default KeywordSearchPageV2;
